<template>
  <div class="plan-wrapper">
    <!-- 顶部 -->
    <div class="header">
      <div class="header-left">
        <a-button icon="arrow-left" size="small" @click="$router.back()" />
        <span class="page-type">活动策划</span>
      </div>
    </div>

    <div v-if="loading" class="loading-box">
      <a-spin />
    </div>

    <div v-else class="content">
      <!-- 基本信息 -->
      <div class="common">
        <div class="title gap-12">
          基本信息
          <a-button type="link" size="small" class="edit-btn" @click="handleEditBasic">
            <a-icon type="edit" />修改
          </a-button>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">活动标题</span>
            <span class="info-value">{{ detail.name || '--' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">有效期</span>
            <span class="info-value">
              <template v-if="detail.isPermanent">永久有效</template>
              <template v-else>{{ formatDate(detail.startDate) }} ~ {{ formatDate(detail.endDate) }}</template>
            </span>
          </div>
          <div class="info-item info-item--full">
            <span class="info-label">活动描述</span>
            <span class="info-value">{{ detail.description || '--' }}</span>
          </div>
        </div>
      </div>

      <!-- 活动内容 -->
      <div class="common">
        <div class="title gap-12">活动内容</div>
        <p class="section-hint">请上传需要分享的资料</p>

        <table class="material-table">
          <thead>
            <tr>
              <th style="width: 200px">*资料名称</th>
              <th>*文件</th>
              <th style="width: 140px">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in editMaterials" :key="idx">
              <!-- 资料名称 -->
              <td>
                <a-input
                  v-model="item.name"
                  placeholder="请输入资料名称"
                  :maxLength="100"
                  size="small"
                />
              </td>

              <!-- 文件上传 -->
              <td>
                <div v-if="item.fileUrl" class="file-done">
                  <a-icon type="paper-clip" style="color: #237ffa; margin-right: 4px" />
                  <span class="file-name-text">{{ item.fileName || item.fileUrl }}</span>
                </div>
                <div v-else class="upload-cell">
                  <el-upload
                    :action="uploadDomain"
                    :data="getUploadData(idx)"
                    :before-upload="(file) => handleBeforeUpload(file, idx)"
                    :on-success="(res) => handleUploadSuccess(res, idx)"
                    :on-error="handleUploadError"
                    :show-file-list="false"
                    drag
                    :accept="acceptTypes"
                    class="dragger-upload"
                  >
                    <div class="dragger-content">
                      <a-icon type="inbox" class="dragger-icon" />
                      <p class="dragger-text">拖拽文件到此，或 <em>点击上传</em></p>
                      <p class="dragger-hint">支持 PDF、Word、Excel、PPT 等，单文件不超过 10MB</p>
                    </div>
                  </el-upload>
                  <div v-if="uploadingIdx === idx" class="uploading-mask">
                    <a-icon type="loading" style="font-size: 20px; color: #237ffa" />
                    <span style="margin-left: 8px; color: #237ffa">上传中...</span>
                  </div>
                </div>
              </td>

              <!-- 操作 -->
              <td class="action-col">
                <template v-if="item.fileUrl">
                  <a
                    :href="getMaterialPreviewUrl(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="action-link"
                  >预览</a>
                  <a class="action-link" @click.prevent="clearFile(idx)">重新上传</a>
                </template>
                <template v-else-if="savedFiles[idx]">
                  <a class="action-link" @click.prevent="cancelReupload(idx)">取消</a>
                </template>
                <a class="action-link action-link--danger" @click.prevent="removeMaterial(idx)">删除</a>
              </td>
            </tr>

            <!-- 空行提示 -->
            <tr v-if="editMaterials.length === 0">
              <td colspan="3" class="empty-row">暂无资料，请点击下方"+"添加</td>
            </tr>
          </tbody>
        </table>

        <!-- 添加行 -->
        <div class="add-row-btn" @click="addMaterial">
          <a-icon type="plus-circle" />
        </div>
      </div>

      <!-- 底部操作 -->
      <div class="common footer-bar">
        <a-button @click="$router.back()" style="margin-right: 12px">取消</a-button>
        <a-button type="primary" :loading="saveLoading" @click="handleSave">保存</a-button>
      </div>
    </div>

    <!-- 修改基本信息弹窗 -->
    <a-modal
      :visible="basicVisible"
      title="修改基本信息"
      :width="520"
      :confirmLoading="basicLoading"
      @ok="handleBasicSubmit"
      @cancel="basicVisible = false"
    >
      <a-form-model
        ref="basicForm"
        :model="basicForm"
        :rules="basicRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item label="活动标题" prop="name">
          <a-input v-model="basicForm.name" placeholder="请输入活动标题" :maxLength="50" />
        </a-form-model-item>
        <a-form-model-item label="活动有效期" prop="isPermanent">
          <a-radio-group v-model="basicForm.isPermanent">
            <a-radio :value="false">指定时间段</a-radio>
            <a-radio :value="true">永久有效</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item v-if="!basicForm.isPermanent" label="时间段" prop="dateRange">
          <a-range-picker
            v-model="basicForm.dateRange"
            style="width: 100%"
            :placeholder="['开始日期', '结束日期']"
            :disabled-date="disabledDate"
          />
        </a-form-model-item>
        <a-form-model-item label="活动描述" prop="description">
          <a-textarea
            v-model="basicForm.description"
            :rows="3"
            placeholder="请输入活动描述（选填）"
            :maxLength="500"
          />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import moment from 'moment';
import Common from '@/api/common';
import { activityDetail, activityUpdate, activitySaveMaterials } from '@/api/activity.js';

export default {
  name: 'ActivityPlan',

  data () {
    return {
      loading: true,
      detail: {},
      editMaterials: [],
      saveLoading: false,

      // 上传相关
      uploadDomain: 'https://upload.qiniup.com',
      qiniuToken: '',
      downloadDomain: '',
      uploadingIdx: -1,
      pendingFileNames: {}, // idx -> 原始文件名，用于上传成功后回填
      savedFiles: {}, // idx -> {fileUrl, fileName}，点击重新上传时暂存，用于取消还原
      acceptTypes: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg',

      // 修改基本信息
      basicVisible: false,
      basicLoading: false,
      basicForm: {
        name: '',
        isPermanent: false,
        dateRange: [],
        description: '',
      },
      basicRules: {
        name: [{ required: true, message: '请输入活动标题', trigger: 'blur' }],
        isPermanent: [{ required: true, message: '请选择有效期类型', trigger: 'change' }],
        dateRange: [
          {
            validator: (rule, value, cb) => {
              if (!this.basicForm.isPermanent && (!value || value.length !== 2)) {
                cb('请选择活动时间段');
              } else {
                cb();
              }
            },
            trigger: 'change',
          },
        ],
      },
    };
  },

  computed: {
    activityId () {
      return this.$route.query.id;
    },
  },

  async created () {
    await this.loadDetail();
    await this.initQiniuToken();
  },

  methods: {
    disabledDate (current) {
      return current && current.valueOf() < dayjs().startOf('day').valueOf();
    },

    async loadDetail () {
      this.loading = true;
      try {
        const { body } = await activityDetail({ id: this.activityId });
        this.detail = body || {};
        this.editMaterials = (body?.materials || []).map((m) => ({ ...m }));
      } finally {
        this.loading = false;
      }
    },

    async initQiniuToken () {
      try {
        const res = await Common.getQinNiuToken();
        this.qiniuToken = res.body.token;
        this.downloadDomain = res.body.domain;
        localStorage.setItem('timestamp', new Date().getTime());
      } catch (e) {
        this.$message.error('获取上传凭证失败，请刷新页面重试');
      }
    },

    getUploadData (idx) {
      return {
        token: this.qiniuToken,
        key: `activity/material/${this.activityId}/${new Date().getTime()}_${idx}`,
      };
    },

    async handleBeforeUpload (file, idx) {
      const allowedExts = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|png|jpg|jpeg)$/i;
      if (!allowedExts.test(file.name)) {
        this.$message.warning('仅支持 PDF、Word、Excel、PPT、图片格式');
        return false;
      }
      if (file.size / 1024 / 1024 > 10) {
        this.$message.warning('文件大小不能超过 10MB');
        return false;
      }
      // Token 过期（20分钟）则刷新
      const now = new Date().getTime();
      const saved = localStorage.getItem('timestamp');
      if (!saved || now - saved > 1000 * 60 * 20) {
        await this.initQiniuToken();
      }
      // 记录原始文件名，供上传成功后回填
      this.$set(this.pendingFileNames, idx, file.name);
      this.uploadingIdx = idx;
      return true;
    },

    handleUploadSuccess (response, idx) {
      this.uploadingIdx = -1;
      const fileUrl = `${this.downloadDomain}/${response.key}`;
      const item = this.editMaterials[idx];
      const fileName = this.pendingFileNames[idx] || response.key.split('/').pop();
      this.$delete(this.pendingFileNames, idx);
      this.$delete(this.savedFiles, idx);
      this.$set(this.editMaterials, idx, { ...item, fileUrl, fileName });
    },

    handleUploadError () {
      this.uploadingIdx = -1;
      this.$message.error('文件上传失败，请重试');
    },

    clearFile (idx) {
      const item = this.editMaterials[idx];
      this.$set(this.savedFiles, idx, { fileUrl: item.fileUrl, fileName: item.fileName });
      this.$set(this.editMaterials, idx, { ...item, fileUrl: '', fileName: '' });
    },

    cancelReupload (idx) {
      const saved = this.savedFiles[idx];
      if (saved) {
        const item = this.editMaterials[idx];
        this.$set(this.editMaterials, idx, { ...item, fileUrl: saved.fileUrl, fileName: saved.fileName });
        this.$delete(this.savedFiles, idx);
      }
    },

    addMaterial () {
      this.editMaterials.push({
        name: '',
        fileUrl: '',
        fileName: '',
        sort: this.editMaterials.length + 1,
      });
    },

    removeMaterial (idx) {
      this.editMaterials.splice(idx, 1);
    },

    async handleSave () {
      // 校验
      for (let i = 0; i < this.editMaterials.length; i++) {
        const m = this.editMaterials[i];
        if (!m.name || !m.name.trim()) {
          this.$message.error(`第 ${i + 1} 行资料名称不能为空`);
          return;
        }
        if (!m.fileUrl) {
          this.$message.error(`第 ${i + 1} 行文件未上传`);
          return;
        }
      }
      // 校验资料名称不能重复
      const names = this.editMaterials.map((m) => m.name.trim());
      const dupName = names.find((n, i) => names.indexOf(n) !== i);
      if (dupName) {
        this.$message.error(`资料名称「${dupName}」重复，请修改后再保存`);
        return;
      }
      this.saveLoading = true;
      try {
        const materials = this.editMaterials.map((m, idx) => ({
          name: m.name,
          fileUrl: m.fileUrl,
          fileName: m.fileName,
          sort: idx + 1,
        }));
        await activitySaveMaterials({ activityId: this.activityId, materials });
        this.$message.success('保存成功');
        this.$router.push({ name: 'ACTIVITY_LIST' });
      } finally {
        this.saveLoading = false;
      }
    },

    // 修改基本信息
    handleEditBasic () {
      this.basicForm = {
        name: this.detail.name,
        isPermanent: this.detail.isPermanent,
        dateRange: this.detail.isPermanent
          ? []
          : [moment(this.detail.startDate), moment(this.detail.endDate)],
        description: this.detail.description || '',
      };
      this.basicVisible = true;
    },

    handleBasicSubmit () {
      this.$refs.basicForm.validate(async (valid) => {
        if (!valid) return;
        this.basicLoading = true;
        try {
          const params = {
            id: this.activityId,
            name: this.basicForm.name,
            isPermanent: this.basicForm.isPermanent,
            startDate: this.basicForm.isPermanent
              ? undefined
              : this.basicForm.dateRange[0].valueOf(),
            endDate: this.basicForm.isPermanent
              ? undefined
              : this.basicForm.dateRange[1].valueOf(),
            description: this.basicForm.description,
          };
          await activityUpdate(params);
          this.$message.success('修改成功');
          this.basicVisible = false;
          await this.loadDetail();
        } finally {
          this.basicLoading = false;
        }
      });
    },

    formatDate (date) {
      return date ? dayjs(date).format('YYYY-MM-DD') : '--';
    },

    getMaterialPreviewUrl (item) {
      if (!item.fileUrl) return '#';
      const fileName = item.fileName || '';
      if (/\.(pdf|png|jpg|jpeg|gif|webp|svg)$/i.test(fileName)) return item.fileUrl;
      if (!fileName) return item.fileUrl;
      const sep = item.fileUrl.includes('?') ? '&' : '?';
      return `${item.fileUrl}${sep}attname=${encodeURIComponent(fileName)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.plan-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;

  // ── 顶部栏 ──────────────────────────────────────
  .header {
    height: 52px;
    background-color: #fff;
    display: flex;
    align-items: center;
    padding: 0 12px;
    flex-shrink: 0;

    .header-left {
      display: flex;
      align-items: center;
    }

    .page-type {
      font-size: 16px;
      font-weight: bold;
      margin-left: 1em;
    }
  }

  // ── 主内容区 ─────────────────────────────────────
  .content {
    flex: 1;
    padding: 8px 0 0;
    overflow: auto;

    .gap-12 {
      margin-bottom: 12px;
    }

    .common {
      background-color: #fff;
      padding: 12px;
      margin-bottom: 6px;

      .title {
        font-weight: bold;
        font-size: 14px;
        color: #1f2733;
        display: flex;
        align-items: center;

        .edit-btn {
          margin-left: 8px;
          font-size: 13px;
          font-weight: normal;
          padding: 0 4px;
          height: auto;
          line-height: 1;
          color: #237ffa;

          .anticon {
            margin-right: 3px;
            font-size: 12px;
          }

          &:hover {
            color: #1a6fdc;
          }
        }
      }
    }
  }

  .loading-box {
    flex: 1;
    display: flex;
    justify-content: center;
    padding-top: 80px;
  }

  // ── 基本信息行 ────────────────────────────────────
  .info-grid {
    display: flex;
    flex-wrap: wrap;
    margin-top: 4px;
  }

  .info-item {
    width: 33.333%;
    display: flex;
    align-items: flex-start;
    font-size: 14px;
    padding: 8px 12px 8px 0;
    line-height: 1.6;

    &--full {
      width: 100%;
    }
  }

  .info-label {
    flex-shrink: 0;
    width: 70px;
    color: #606266;
    margin-right: 4px;

    &::after {
      content: '：';
    }
  }

  .info-value {
    flex: 1;
    min-width: 0;
    color: #303133;
    word-break: break-word;
  }

  // ── 内容区提示文字 ────────────────────────────────
  .section-hint {
    font-size: 13px;
    color: #929aa6;
    margin-bottom: 12px;
  }

  // ── 资料表格 ──────────────────────────────────────
  .material-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #f0f0f0;

    th,
    td {
      border: 1px solid #f0f0f0;
      padding: 10px 12px;
      vertical-align: middle;
    }

    th {
      background: #fafafa;
      font-size: 13px;
      font-weight: 500;
      color: #555;
    }
  }

  .empty-row {
    text-align: center;
    color: #929aa6;
    padding: 20px 0 !important;
    font-size: 13px;
  }

  // ── 已上传文件展示 ────────────────────────────────
  .file-done {
    display: flex;
    align-items: center;
    font-size: 13px;
  }

  .file-name-text {
    color: #1f2733;
    word-break: break-all;
    flex: 1;
    min-width: 0;
  }

  // ── 操作列 ────────────────────────────────────────
  .action-col {
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
  }

  .action-link {
    display: inline-block;
    font-size: 13px;
    color: #237ffa;
    cursor: pointer;
    padding: 0 6px;

    &:hover {
      color: #1a6fdc;
    }

    & + & {
      border-left: 1px solid #e8e8e8;
    }

    &--danger {
      color: #ff4d4f;

      &:hover {
        color: #cf1322;
      }
    }
  }

  // ── 上传拖拽区 ────────────────────────────────────
  .upload-cell {
    position: relative;
  }

  .dragger-upload {
    width: 100%;

    /deep/ .el-upload {
      width: 100%;
    }

    /deep/ .el-upload-dragger {
      width: 100%;
      height: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px dashed #d9d9d9;
      border-radius: 4px;
      background: #fafafa;
      cursor: pointer;
      transition: border-color 0.2s;

      &:hover {
        border-color: #237ffa;
      }
    }
  }

  .dragger-content {
    text-align: center;
    pointer-events: none;
  }

  .dragger-icon {
    font-size: 28px;
    color: #237ffa;
    margin-bottom: 4px;
  }

  .dragger-text {
    font-size: 13px;
    color: #555;
    margin: 0;

    em {
      color: #237ffa;
      font-style: normal;
    }
  }

  .dragger-hint {
    font-size: 11px;
    color: #bbb;
    margin: 2px 0 0;
  }

  // ── 上传中遮罩 ────────────────────────────────────
  .uploading-mask {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
  }

  // ── 添加行按钮 ────────────────────────────────────
  .add-row-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 12px auto 4px;
    border: 1px dashed #d9d9d9;
    border-radius: 50%;
    cursor: pointer;
    color: #237ffa;
    font-size: 18px;
    transition: border-color 0.2s, color 0.2s;

    &:hover {
      border-color: #237ffa;
    }
  }

  // ── 底部保存栏 ────────────────────────────────────
  .footer-bar {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
